/**
 * MAX SECURITY — THE ONE DECISION.
 *
 * Erez, 2026-08-04: the whole engagement's connectivity question collapsed to a
 * single choice, so these two diagrams exist to make that choice visible and
 * nothing else. Everything downstream — the read-only users, the MCP wiring, the
 * work itself — is identical on both paths. Only ONE box differs, and it is the
 * only box that matters:
 *
 *      WHERE DOES THE MODEL RUN, AND THEREFORE WHERE DOES THEIR DATA GO?
 *
 *   A · Bedrock      — the model runs in MAX's own AWS. Their data never leaves
 *                      their cloud. AWS is sole processor. No Anthropic account
 *                      exists on this path at all; Claude Code signs in with AWS
 *                      credentials and is billed as AWS tokens.
 *   B · Claude Team  — the model runs at Anthropic. Their prompts and responses
 *                      cross out to Anthropic under contract. Billed per seat.
 *                      What that buys is a polished app for THEIR people.
 *
 * Both diagrams are laid out identically on purpose — same actor, same right-hand
 * systems column — so the eye lands on the single column that changed. Red marks
 * the data crossing out of MAX's control; it appears in exactly one diagram.
 *
 * Nothing here has a hand-written width (see ./c4). Each diagram is a data object.
 */
import { Canvas, leaf, person, box, type Diagram } from "./c4";

/** The four systems we want read-only users on. Identical on both paths. */
const systemsCol = (p: string) =>
  box(`${p}-sys`, "MAX'S SYSTEMS", "col", [
    leaf(`${p}-hub`, "HubSpot", "[read-only user]", "Contact"),
    leaf(`${p}-mon`, "Monday", "[read-only user]", "KanbanSquare"),
    leaf(`${p}-out`, "Outlook", "[read-only user]", "Mail"),
    leaf(`${p}-fire`, "Firefly", "[read-only user]", "Mic"),
  ], { gap: 11 });

// ═══ A · BEDROCK — the model runs in their cloud ═════════════════════════════

const BEDROCK: Diagram = (() => {
  const actor = person("a-us", "Us · operator");

  const ours = box("a-ours", "OUR MACHINE", "col", [
    leaf("a-code", "Claude Code", "[CLAUDE_CODE_USE_BEDROCK=1]", "Terminal"),
    leaf("a-mcp", "MCP clients", "[OAuth per system]", "Plug"),
  ], { gap: 12, tech: "[no Anthropic account]" });

  const aws = box("a-aws", "MAX'S OWN AWS", "col", [
    leaf("a-br", "Claude on Bedrock", "[il-central-1 · Tel Aviv]", "Cpu"),
    leaf("a-trail", "CloudTrail", "[every call logged]", "ScrollText"),
  ], { gap: 12, tech: "[AWS is sole processor]" });

  return {
    key: "bed",
    title: "A · Amazon Bedrock — the model runs in their cloud",
    subtitle: "Their data never leaves MAX · billed as AWS tokens · no subscription, no seats",
    padBottom: 80,
    cols: [
      { root: actor, align: "center" },
      { root: ours, align: "center" },
      { root: aws, align: "center" },
      { root: systemsCol("a"), align: "center" },
    ],
    wires: [
      { a: "a-us", b: "a-code", as: "r", bs: "l", label: "terminal" },
      { a: "a-code", b: "a-br", as: "r", bs: "l", label: "SigV4 · AWS creds" },
      { a: "a-mcp", b: "a-hub", as: "b", bs: "l", label: "OAuth · read-only" },
    ],
    legend: [
      { style: "solid", label: "connection path" },
      { style: "dash", label: "nothing on this path reaches Anthropic" },
    ],
  };
})();

// ═══ B · CLAUDE TEAM — the model runs at Anthropic ═══════════════════════════

const TEAM: Diagram = (() => {
  const actor = person("b-us", "Us · operator");

  const ours = box("b-ours", "OUR MACHINE", "col", [
    leaf("b-app", "Claude app", "[a seat in their org]", "Laptop"),
    leaf("b-conn", "Connectors", "[OAuth per system]", "Plug"),
  ], { gap: 12, tech: "[MAX admins the org]" });

  const anthropic = box("b-anth", "ANTHROPIC", "col", [
    leaf("b-model", "Claude models", "[Anthropic-hosted]", "Cpu"),
    leaf("b-cross", "PROMPTS + RESPONSES", "[leave MAX's control]", "ShieldAlert", "gate"),
  ], { gap: 12, tech: "[no training · 30-day retention]" });

  return {
    key: "team",
    title: "B · Claude Team — the model runs at Anthropic",
    subtitle: "Billed per seat · buys a polished app for THEIR people · ZDR only on Enterprise",
    padBottom: 80,
    cols: [
      { root: actor, align: "center" },
      { root: ours, align: "center" },
      { root: anthropic, align: "center" },
      { root: systemsCol("b"), align: "center" },
    ],
    wires: [
      { a: "b-us", b: "b-app", as: "r", bs: "l", label: "their SSO" },
      { a: "b-app", b: "b-cross", as: "r", bs: "l", label: "HTTPS", red: true },
      { a: "b-conn", b: "b-hub", as: "b", bs: "l", label: "OAuth · read-only" },
    ],
    legend: [
      { style: "solid", label: "connection path" },
      { style: "red", label: "their data crossing out of MAX's control" },
    ],
  };
})();

export function MaxConnectBedrock() { return <Canvas d={BEDROCK} mode="contrast" />; }
export function MaxConnectTeam() { return <Canvas d={TEAM} mode="contrast" />; }
